"use client";

import React from "react";
import { EntityLinkChip } from "./EntityLinkChip";
import { EntityPreviewCard } from "./EntityPreviewCard";
import { EntitySearchResultsCard } from "./EntitySearchResultsCard";
import { ChatDataTableCard } from "./ChatDataTableCard";
import { ChatTimeseriesChartCard } from "./ChatTimeseriesChartCard";
import { TaskProposalCard } from "./TaskProposalCard";
import type { ChatEmbed, ChatDataTableEmbed, ChatTimeseriesChartEmbed } from "./types";
import { shouldShowStandaloneAnalyticsEmbed } from "./dataTableEmbedUi";

function isStandaloneDataTableEmbed(embed: ChatEmbed): embed is ChatDataTableEmbed {
  return embed.kind === "data_table" && shouldShowStandaloneAnalyticsEmbed(embed);
}

function isStandaloneTimeseriesChartEmbed(embed: ChatEmbed): embed is ChatTimeseriesChartEmbed {
  return embed.kind === "timeseries_chart" && shouldShowStandaloneAnalyticsEmbed(embed);
}

type ChatEmbedsProps = {
  embeds: ChatEmbed[];
  messageId?: string;
  /** Timeline segments use tighter spacing to match tool-step rows. */
  variant?: "default" | "timeline";
};

export function ChatEmbeds({ embeds, messageId, variant = "default" }: ChatEmbedsProps) {
  if (!embeds.length) return null;
  const containerClass =
    variant === "timeline" ? "space-y-1" : "mt-2 mb-4 space-y-2";

  const links = embeds.filter((e) => e.kind === "link");
  const searchResults = embeds.filter((e) => e.kind === "entity_search_results");
  const detailPreviews = embeds.filter((e) => e.kind === "entity_preview");
  const taskProposals = embeds.filter((e) => e.kind === "task_proposal");
  const dataTables = embeds.filter(isStandaloneDataTableEmbed);
  const timeseriesCharts = embeds.filter(isStandaloneTimeseriesChartEmbed);

  return (
    <div className={containerClass} data-testid="chat-embeds">
      {links.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {links.map((embed, i) => (
            <EntityLinkChip key={`${embed.id}-${i}`} embed={embed} />
          ))}
        </div>
      ) : null}

      {searchResults.map((embed, i) => (
        <EntitySearchResultsCard
          key={`search-${embed.entity_type}-${i}`}
          embed={embed}
          cardIndex={i}
        />
      ))}

      {detailPreviews.map((embed, i) => (
        <EntityPreviewCard
          key={`${embed.entity_type}-${embed.id}-${i}`}
          embed={embed}
          messageId={messageId}
          cardIndex={i}
        />
      ))}

      {taskProposals.map((embed, i) => (
        <TaskProposalCard
          key={`${embed.catalogue_id}-${i}`}
          embed={embed}
          messageId={messageId}
          embedIndex={i}
        />
      ))}

      {dataTables.map((embed, i) => (
        <ChatDataTableCard key={`data-table-${i}`} embed={embed} />
      ))}

      {timeseriesCharts.map((embed, i) => (
        <ChatTimeseriesChartCard key={`timeseries-chart-${i}`} embed={embed} />
      ))}
    </div>
  );
}
